import {getAgoraToken, leaveChannel, initializeClient, joinChannel} from "./agora/agoraClient";
import {
  setupFireStoreListener,
  fetchAppCheckToken,
  CallStatus,
  CallStatusListenerCallbacks,
} from "./firebase/FirebaseService";
import {getNotificationProvider} from "./utils";
import axios from "axios";

const notificationProvider = getNotificationProvider();

interface CallRequest {
  corlmiNumber: string;
  endCall: (error: string | undefined) => void;
  onConnected: () => void;
  onActive?: () => void;
}

interface CallResponse {
  corlmiNumber: string;
  channel: string;
  signalingChannel?: string;
  unsubscribe?: () => void;
}

const generateRandomNumber = (len: number) => {
  const characters = "0123456789abcdefghijklmnopqrstuvwxyz";
  let result = "";
  const length = len;

  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }

  return result;
};

// Start the basic call process (immediate join flow, for testing / legacy)
async function startBasicCall(
  endCallback: (error?: string) => void,
  connectedCallback: () => void,
  appId: string,
  corlmiNumber: string,
  token: string,
  uid: string,
  channel: string
) {
  try {
    initializeClient(endCallback, connectedCallback);
    console.log("Joining channel...");
    await joinChannel(appId, channel, token, uid);
    console.log("Joined channel.", channel);
    try {
      await sendNotification(corlmiNumber, channel);
    } catch (notificationError: any) {
      console.warn(
        "Notification failed, terminating call:",
        notificationError
      );
      leaveChannel();
      const errorMessage = notificationError.response?.data?.message || notificationError?.message || "Error sending notification";
      endCallback(errorMessage);
    }
  } catch (e: any) {
    console.error("Error joining channel", e);
    leaveChannel();
    endCallback(e?.message || "Error");
  }
}

async function sendNotification(
  corlmiNumber: string,
  channel: string,
  signalingChannel?: string
) {
  console.debug(`Sending notification to ${corlmiNumber}`);
  const url = `${process.env.REACT_APP_CORLMI_API_URL}/api/v2/send-notification`;
  const headers = {
    "Content-Type": "application/json",
    "x-firebase-appcheck": await fetchAppCheckToken(),
  };
  const data = {
    corlmiNumber,
    channel,
    signalingChannel,
    callerName: "Web Caller",
  };
  console.debug(
    `Sending notification payload: ${JSON.stringify(data)} to ${url}`
  );
  const response = await axios.post(url, data, {headers: headers});
  console.log(
    `Notification Received response: ${JSON.stringify(response?.data)}`
  );
}

const makeCall = async (data: CallRequest): Promise<CallResponse> => {
  const tokenData = await getAgoraToken(data.corlmiNumber);

  console.debug(
    `token: ${tokenData.token}, agorauid: ${tokenData.agorauid}, agoraId: ${tokenData.agoraId}, channelName: ${tokenData.channelName}, signalingChannel: ${tokenData.signalingChannel}`
  );

  if (!tokenData.token) {
    console.error("Error generating token");
    throw new Error("Error generating token");
  }

  // Pre-initialize RTC Client listeners so they are ready when joined
  initializeClient(data.endCall, data.onConnected);

  // Send push notification to the receiver device
  try {
    await sendNotification(
      data.corlmiNumber,
      tokenData.channelName,
      tokenData.signalingChannel
    );
  } catch (notificationError: any) {
    console.warn("Notification failed, terminating call:", notificationError);
    leaveChannel();
    const errorMessage =
      notificationError.response?.data?.error ||
      "Unable to initiate call";
    data.endCall(errorMessage);
    throw new Error(errorMessage);
  }

  // Defer Agora channel join until the receiver requests their token (status: "active")
  let unsubscribe: (() => void) | undefined;
  if (notificationProvider === "firestore") {
    const signalingId = tokenData.signalingChannel || tokenData.channelName;
    unsubscribe = setupListener(signalingId, {
      onActive: async () => {
        console.log(
          `[callService] Receiver requested token (active). Joining Agora channel [${tokenData.channelName}]...`
        );
        if (data.onActive) {
          data.onActive();
        }
        try {
          await joinChannel(
            tokenData.agoraId,
            tokenData.channelName,
            tokenData.token,
            tokenData.agorauid
          );
          console.log(
            `[callService] Joined Agora channel [${tokenData.channelName}] successfully.`
          );
        } catch (joinError: any) {
          console.error(
            "[callService] Error joining Agora channel on receiver active:",
            joinError
          );
          leaveChannel();
          data.endCall(joinError?.message || "Failed to join audio channel");
        }
      },
      onCallFailed: (status) => {
        console.log(`[callService] Call failed/ended with status: ${status}`);
        leaveChannel();
        if (status === CallStatus.BUSY) {
          data.endCall("User is busy");
        } else if (status === CallStatus.REJECTED) {
          data.endCall("Call rejected");
        } else if (status === CallStatus.CANCELED) {
          data.endCall("Call canceled");
        } else {
          data.endCall("Call ended");
        }
      },
    });
  }

  return {
    corlmiNumber: data.corlmiNumber,
    channel: tokenData.channelName,
    signalingChannel: tokenData.signalingChannel,
    unsubscribe,
  };
};

function setupListener(
  channel: string,
  callbacks: CallStatusListenerCallbacks
): (() => void) {
  return setupFireStoreListener(channel, callbacks);
}

async function cancelCall(channel: string): Promise<void> {
  console.debug(`Canceling call for channel ${channel}`);
  const url = `${process.env.REACT_APP_CORLMI_API_URL}/api/call/cancel`;
  const headers = {
    "Content-Type": "application/json",
    "x-firebase-appcheck": await fetchAppCheckToken(),
  };
  const data = {channel, signalingChannel: channel};
  console.debug(`Sending cancel call payload: ${JSON.stringify(data)} to ${url}`);
  const response = await axios.post(url, data, {headers: headers});
  console.log(`Cancel Call response: ${JSON.stringify(response?.data)}`);
}

export {generateRandomNumber, makeCall, startBasicCall, cancelCall};

